import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    ClassificationDetails,
    patternChip,
    selectPostureIntegratedClassification,
} from "@/components/posture-analysis/ClassificationDetails";
import { AnnotatedImage } from "@/components/posture-analysis/AnnotatedImage";
import type { PostureAnalysis, CaptureSequence, Analysis } from "@/lib/types";
import type { SopAnalysisResult } from "@/components/sop-intake/sop-analyzer/types";
import { formatDateTimeLocale } from "@/lib/utils";
import { ChevronUp, ChevronDown, Table2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { buildSopSidebarSummaryLines } from "@/components/posture-analysis/sop-sidebar-summary";
import { usePostureMode } from "@/components/posture-analysis/lib/PostureModeContext";

interface ClientPostureOverviewProps {
    analysis: PostureAnalysis | null;
    classification: any;
    layout?: "sidebar" | "header" | "drawer";
    /** When true, show Posture and SOP Analysis block when summary or fused classification exists. */
    showIntegratedSopSummary?: boolean;
    /** SOP analyzer pipeline result (rich lateralization tests). */
    sopAnalyzerAnalysis?: SopAnalysisResult | null;
    /** Legacy bucket analysis from `computeSopAnalysis`. */
    sopBucketAnalysis?: Analysis | null;
}

export const ClientPostureOverview: React.FC<ClientPostureOverviewProps> = ({
    analysis,
    classification,
    layout = "sidebar",
    showIntegratedSopSummary = false,
    sopAnalyzerAnalysis = null,
    sopBucketAnalysis = null,
}) => {
    const [isPostureSopExpanded, setIsPostureSopExpanded] = useState(true);
    const { laymanMode } = usePostureMode();

    const sopSummaryLines = useMemo(
        () => buildSopSidebarSummaryLines({ sopAnalyzerAnalysis, sopBucketAnalysis }),
        [sopAnalyzerAnalysis, sopBucketAnalysis]
    );

    const integratedTileClassification = classification
        ? selectPostureIntegratedClassification(classification)
        : null;

    const showPostureSopBlock =
        showIntegratedSopSummary &&
        (!!integratedTileClassification || sopSummaryLines.length > 0);

    const photoSnippet =
        !laymanMode && classification?.picturePosture?.reasoningShort?.global?.trim()
            ? classification.picturePosture.reasoningShort.global.trim()
            : null;

    if (!analysis && !classification) {
        return (
            <Card className="h-full bg-muted/50 border-dashed">
                <CardContent className="flex flex-col items-center justify-center p-6 text-center h-full min-h-[200px]">
                    <p className="text-muted-foreground text-sm">No posture analysis data available yet.</p>
                </CardContent>
            </Card>
        );
    }

    const getBestView = (): CaptureSequence | null => {
        if (!analysis?.captured) return null;
        if (analysis.captured['Left']?.imageData) return 'Left';
        if (analysis.captured['Right']?.imageData) return 'Right';
        if (analysis.captured['Anterior']?.imageData) return 'Anterior';
        if (analysis.captured['Posterior']?.imageData) return 'Posterior';
        return null;
    };

    const bestView = getBestView();

    const renderVisuals = () => {
        if (!analysis || !bestView) {
            return (
                <div className="bg-muted rounded-md aspect-square flex flex-col items-center justify-center p-4">
                    <p className="text-xs text-muted-foreground">No image</p>
                </div>
            );
        }
        return (
            <div className="bg-muted rounded-md overflow-hidden flex justify-center p-2 mb-4">
                <AnnotatedImage view={bestView} analysis={analysis} />
            </div>
        );
    };

    const integratedChips = integratedTileClassification
        ? (() => {
            const chip = patternChip(integratedTileClassification.pattern);
            const chips: Array<{ label: string; className: string }> = [chip];
            if (integratedTileClassification.lateralized && integratedTileClassification.lateralized !== 'Balanced') {
                chips.push({ label: integratedTileClassification.lateralized, className: 'bg-muted text-foreground border-border' });
            }
            return chips;
        })()
        : null;

    const renderPostureSopAnalysisTile = () => {
        if (!showPostureSopBlock) return null;

        const collapsedChips = integratedChips;

        return (
            <div className="rounded-xl border bg-card overflow-hidden">
                <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-muted/40 transition-colors"
                    onClick={() => setIsPostureSopExpanded((v) => !v)}
                    aria-expanded={isPostureSopExpanded}
                >
                    <Table2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium shrink-0">Posture and SOP Analysis</span>
                    {!isPostureSopExpanded && (
                        <div className="flex-1 flex items-center gap-1.5 overflow-hidden">
                            {collapsedChips ? (
                                collapsedChips.map((c, i) => (
                                    <Badge
                                        key={i}
                                        variant="outline"
                                        className={`border text-xs px-2 py-0 shrink-0 ${c.className}`}
                                    >
                                        {c.label}
                                    </Badge>
                                ))
                            ) : (
                                <span className="text-xs text-muted-foreground italic truncate">SoP highlights…</span>
                            )}
                        </div>
                    )}
                    {isPostureSopExpanded && <div className="flex-1" />}
                    {isPostureSopExpanded ? (
                        <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                </button>
                {isPostureSopExpanded && (
                    <div className="px-4 pb-4 border-t">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide pt-3 pb-1">
                            SoP summary
                        </p>
                        {sopSummaryLines.length > 0 ? (
                            <ul className="space-y-1 mb-3 text-xs leading-snug text-foreground">
                                {sopSummaryLines.map((line, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs text-muted-foreground mb-3">
                                Enter tests to see a SoP summary.
                            </p>
                        )}
                        {photoSnippet ? (
                            <p className="text-xs text-muted-foreground mb-3 border-l-2 border-border pl-2 break-words line-clamp-2">
                                <span className="font-medium text-foreground/80">Photo (quick read): </span>
                                {photoSnippet}
                            </p>
                        ) : null}
                        {integratedTileClassification ? (
                            <ClassificationDetails
                                classification={integratedTileClassification}
                                showDetailSections={false}
                            />
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Add lateral posture captures to see fused table + image classification and blended
                                confidences.
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (layout === "header") {
        return (
            <Card className="w-full">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Posture Overview</CardTitle>
                    {analysis?._reanalyzedAt && (
                        <CardDescription className="text-xs">
                            Updated {formatDateTimeLocale(analysis._reanalyzedAt)}
                        </CardDescription>
                    )}
                </CardHeader>
                <CardContent className="flex flex-col md:flex-row gap-6">
                    <div className="w-full md:w-1/4 shrink-0 flex items-center justify-center">
                        {renderVisuals()}
                    </div>
                    <div className="flex-1 w-full space-y-0">
                        {renderPostureSopAnalysisTile()}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full h-full flex flex-col border-none shadow-none bg-transparent">
            <CardContent className="p-0 flex flex-col gap-0 overflow-y-auto">
                <div className="w-full shrink-0 flex items-center justify-center">
                    {renderVisuals()}
                </div>
                <div className="w-full">
                    {renderPostureSopAnalysisTile()}
                </div>
            </CardContent>
        </Card>
    );
};
